import { PublicClientApplication } from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-browser";
import type { AppConfig } from "@/lib/appConfig";

let msal: PublicClientApplication | null = null;
let apiScope = "";

export async function initMsal(config: AppConfig): Promise<PublicClientApplication | null> {
  if (!config.sso.enabled || !config.sso.clientId || !config.sso.authority) return null;
  if (msal) return msal;

  apiScope = config.sso.apiScope ?? "";
  msal = new PublicClientApplication({
    auth: {
      clientId: config.sso.clientId,
      authority: config.sso.authority,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: "localStorage" },
  });
  await msal.initialize();
  await msal.handleRedirectPromise();
  return msal;
}

export function getMsal(): PublicClientApplication | null {
  return msal;
}

export function getActiveAccount(): AccountInfo | null {
  if (!msal) return null;
  const accounts = msal.getAllAccounts();
  return accounts.length > 0 ? (accounts[0] ?? null) : null;
}

export async function loginRedirect(): Promise<void> {
  if (!msal) throw new Error("MSAL not initialized");
  await msal.loginRedirect({ scopes: [apiScope, "openid", "profile", "email"].filter(Boolean) });
}

export async function acquireApiToken(): Promise<string | null> {
  const account = getActiveAccount();
  if (!msal || !account || !apiScope) return null;
  try {
    const result = await msal.acquireTokenSilent({ scopes: [apiScope], account });
    return result.accessToken;
  } catch {
    // Silent renewal failed (expired session, consent change): restart interactive login.
    await msal.acquireTokenRedirect({ scopes: [apiScope], account });
    return null;
  }
}

export async function logoutMsal(): Promise<void> {
  if (!msal) return;
  await msal.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
}
